import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import { getImageProps, type ImageProps as NextImageProps } from "next/image";
import type { ComponentPropsWithoutRef } from "react";

import { breakpoints } from "@/config/breakpoints";
import { cn } from "@/lib/utils";

type ContentfulPictureProps = ComponentPropsWithoutRef<"picture"> & {
  imageProps?: ImageProps;
  desktopImage?: Maybe<ContentfulAssetFragment>;
  mobileImage?: Maybe<ContentfulAssetFragment>;
  mobileBreakpoint?: (typeof breakpoints)[keyof typeof breakpoints];
  media?: string;
};

type ImageProps = Omit<NextImageProps, "src" | "alt" | "loader">;

function assetAlt(image?: Maybe<ContentfulAssetFragment>) {
  return image?.description ?? image?.title ?? "";
}

function contentfulImageProps(
  image: ContentfulAssetFragment,
  imageProps: ImageProps,
  alt: string,
) {
  const {
    className: _className,
    priority,
    fetchPriority,
    ...rest
  } = imageProps;

  return getImageProps({
    ...rest,
    alt,
    src: image.url ?? "",
    width: image.width ?? undefined,
    height: image.height ?? undefined,
    sizes: rest.sizes ?? "100vw",
    priority,
    fetchPriority: priority ? "high" : fetchPriority,
  }).props;
}

export default function ContentfulPicture({
  className,
  imageProps = {},
  desktopImage,
  mobileImage,
  mobileBreakpoint = breakpoints.lg,
  media,
  ...props
}: ContentfulPictureProps) {
  const image = desktopImage?.url ? desktopImage : mobileImage;

  if (!image?.url) {
    return null;
  }

  const alt = assetAlt(desktopImage) || assetAlt(mobileImage);
  const { className: imageClassName } = imageProps;
  const imgProps = contentfulImageProps(image, imageProps, alt);

  if (media) {
    return (
      <picture className={cn("relative", className)} {...props}>
        <source
          media={media}
          srcSet={imgProps.srcSet || imgProps.src}
          sizes={imgProps.sizes}
        />
        <img
          alt={alt}
          width={imgProps.width}
          height={imgProps.height}
          fetchPriority={imgProps.fetchPriority}
          className={cn("object-cover", imageClassName)}
        />
      </picture>
    );
  }

  const mobileSource =
    mobileImage?.url && mobileImage.url !== image.url
      ? contentfulImageProps(mobileImage, imageProps, alt)
      : undefined;

  return (
    <picture className={cn("relative", className)} {...props}>
      {mobileSource?.srcSet ? (
        <source
          media={`(max-width: ${mobileBreakpoint}px)`}
          srcSet={mobileSource.srcSet}
          sizes={mobileSource.sizes}
        />
      ) : null}
      <img
        {...imgProps}
        className={cn("object-cover", imageClassName)}
        alt={alt}
      />
    </picture>
  );
}
